/**
 * The tasks on one shift, with the status control the incumbent interface has
 * (TF-TASK-CONTROLS, corrected by TF-TASK-RECOVERY).
 *
 * What the corrections are about: each task carries its own pending state, so
 * two saves never unlock each other; the status on screen comes from the
 * shift data the app already holds, and the server's own answer stands in
 * only until a refresh confirms it, so nobody's later change is shadowed; and
 * a write whose outcome is unknown leaves that task blocked until a refresh
 * actually succeeds, with an explicit read retry rather than a quiet
 * re-enable. Nothing is created, renamed or deleted here.
 */
import { ApiError } from '@/api/client';
import { type ApiTaskAssignment, useTaskStatus } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { fullName, hhmm } from '@/ui/format';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const OPTIONS: [string, string][] = [
  ['pending', 'Ikke startet'],
  ['in-progress', 'Pågår'],
  ['completed', 'Fullført'],
];

/** idle: the control is usable. saving: its own write is out. settling: written, waiting for a confirmed read. unknown: nobody knows whether it was written, a read must succeed first. unconfirmed: it was written and the refresh failed, so the status is known and the view is not. gone: the task is no longer ours to change. */
type Phase = 'idle' | 'saving' | 'settling' | 'unknown' | 'unconfirmed' | 'gone';
interface RowState {
  phase: Phase;
  /** the server's own answer, held only until a refresh confirms it */
  answer?: ApiTaskAssignment;
  error?: string;
  /** the notification outcome, its own line */
  notice?: string;
  /** what a reconciliation after an ambiguous save settled on, neutral about the cause */
  info?: string;
}

export function Tasks({ tasks }: { tasks: ApiTaskAssignment[] | undefined }) {
  const w = useTaskStatus();
  const qc = useQueryClient();
  const [rows, setRows] = useState<Record<number, RowState>>({});
  const set = (id: number, s: RowState) => setRows((r) => ({ ...r, [id]: s }));
  const phaseOf = (id: number): Phase => rows[id]?.phase ?? 'idle';

  /** Refresh the shift lists and say whether that actually worked. */
  async function reread(): Promise<boolean> {
    const keys = [['my-shifts'], ['shifts']];
    try {
      await Promise.all(keys.map((key) => qc.refetchQueries({ queryKey: key })));
    } catch {
      return false;
    }
    const mine = qc
      .getQueryCache()
      .findAll()
      .filter((q) => keys.some((k) => q.queryKey[0] === k[0]));
    // a read counts only if a shift list actually came back and none of them failed
    return (
      mine.length > 0 &&
      mine.every((q) => q.state.error == null) &&
      mine.some((q) => q.state.data !== undefined)
    );
  }

  async function save(id: number, status: string) {
    if (phaseOf(id) !== 'idle') return; // one write per task at a time; other tasks are untouched
    set(id, { phase: 'saving' });
    let answer: ApiTaskAssignment & { notification?: string };
    try {
      answer = await w.mutateAsync({ id, status });
    } catch (e) {
      const code = e instanceof ApiError ? e.status : undefined;
      if (code === 404 || code === 403) {
        set(id, { phase: 'gone', error: T('Oppgaven er ikke tilgjengelig lenger.') });
        void reread(); // the list itself may no longer carry this task
        return;
      }
      // A refusal the route decided before writing anything: its own validation,
      // and the client's own refusals (a company switch under way, a session
      // changed in another tab). Only these prove nothing was written, so only
      // these return the control to use. A 5xx proves nothing either way: the
      // write may well have been applied before whatever failed (TF-TASK-RECOVERY
      // final review), and neither does a discarded answer or no answer at all.
      if (code != null && code >= 400 && code < 500 && code !== 499) {
        set(id, {
          phase: 'idle',
          error: T('Kunne ikke lagre: {message}', { message: (e as Error).message }),
        });
        return;
      }
      // nobody knows whether it was written: stay blocked until a read succeeds
      set(id, {
        phase: 'unknown',
        error: T('Vi vet ikke om endringen ble lagret. Les på nytt før du prøver igjen.'),
      });
      // the read says what is stored now; it cannot say whether this request did it
      if (await reread())
        set(id, { phase: 'idle', info: T('Status hentet på nytt. Viser det som er lagret.') });
      return;
    }
    const notice =
      answer.notification === 'failed' ? T('Status lagret, men varselet kunne ikke sendes.') : undefined;
    set(id, { phase: 'settling', answer, ...(notice ? { notice } : {}) });
    if (await reread()) set(id, { phase: 'idle', ...(notice ? { notice } : {}) });
    // the server said it saved this; only the refresh failed, so say that and
    // nothing worse. The notification notice, if there is one, stays its own line
    else
      set(id, {
        phase: 'unconfirmed',
        answer,
        error: T('Status lagret. Kunne ikke oppdatere visningen. Prøv igjen.'),
        ...(notice ? { notice } : {}),
      });
  }

  /** The explicit read retry. It changes nothing on the server and keeps the row's own wording if it fails again. */
  async function readAgain(id: number) {
    const was: RowState = rows[id] ?? { phase: 'unknown' };
    set(id, { ...was, phase: 'settling' });
    if (await reread())
      set(id, {
        phase: 'idle',
        ...(was.notice ? { notice: was.notice } : {}),
        // only the ambiguous case needs saying; a confirmed save was never in doubt
        ...(was.phase === 'unknown' ? { info: T('Status hentet på nytt. Viser det som er lagret.') } : {}),
      });
    else set(id, was);
  }

  if (!tasks) return null;
  if (!tasks.length) return <p className="hint">{T('Ingen oppgaver på denne vakten.')}</p>;

  return (
    <div className="tasks">
      {tasks.map((raw) => {
        const st = rows[raw.id] ?? { phase: 'idle' as Phase };
        // the list is the authority; the server's answer stands in only while this row settles
        const settling = st.phase === 'settling' || st.phase === 'unknown' || st.phase === 'unconfirmed';
        const shown = settling ? (st.answer ?? raw) : raw;
        const busy = st.phase !== 'idle';
        const done = shown.completed_at ? oslo(shown.completed_at) : null;
        const who = fullName(shown.completedByUser);
        return (
          <div className="trow" key={raw.id} data-testid="task-row">
            <span className="tname">
              <b>{shown.shiftTask?.name ?? ''}</b>
              {shown.shiftTask?.description ? <span>{shown.shiftTask.description}</span> : null}
              {shown.has_time_range && shown.start_time ? (
                <span className="mono">
                  {hhmm(shown.start_time)}
                  {shown.end_time ? `–${hhmm(shown.end_time)}` : ''}
                </span>
              ) : null}
              {done ? (
                <span>
                  {who
                    ? T('Fullført {time} av {who}', { time: `${done.date} ${done.time}`, who })
                    : T('Fullført {time}', { time: `${done.date} ${done.time}` })}
                </span>
              ) : null}
              {st.info ? <span data-testid="task-info">{st.info}</span> : null}
              {st.notice ? <span data-testid="task-notice">{st.notice}</span> : null}
              {st.error ? (
                <span role="alert" style={{ color: 'var(--dgr-i)' }}>
                  {st.error}
                </span>
              ) : null}
            </span>
            <span className="tstat">
              <select
                className="inp"
                aria-label={T('Status for {task}', { task: shown.shiftTask?.name ?? '' })}
                value={shown.status}
                disabled={busy}
                onChange={(e) => save(raw.id, e.target.value)}
              >
                {OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {T(label)}
                  </option>
                ))}
              </select>
              {st.phase === 'saving' || st.phase === 'settling' ? (
                <span className="sub">{T('Lagrer…')}</span>
              ) : null}
              {st.phase === 'unknown' || st.phase === 'unconfirmed' ? (
                <button type="button" className="btn sm" onClick={() => readAgain(raw.id)}>
                  {T('Prøv igjen')}
                </button>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}
